/**
 * Selectors for meta filters data
 * These selectors extract specific data from the meta filters response
 */
import { createSelector } from '@reduxjs/toolkit'
import { get, groupBy, keyBy, sortBy } from 'lodash'

// Base selectors
const getServiceActions = state => get(state, 'serviceAction', [])
const getMeasures = state => get(state, 'measure', [])
const getMaterials = state => get(state, 'materials', [])
const getMethods = state => get(state, 'methods', [])
const getPricingZones = state => get(state, 'pricingZones', [])
const getBusinessLines = state => get(state, 'businessLines', [])
const getTaxZones = state => get(state, 'taxZones', [])
const getSkills = state => get(state, 'skills', [])
const getPricingPeriod = state => get(state, 'pricingPeriod', [])
const getRecurrence = state => get(state, 'recurrence', [])

/**
 * Selects the serviceAction array
 * @param {Object} data - The meta filters data
 * @returns {Array} - The serviceAction array
 */
export const selectServiceActions = createSelector([getServiceActions], serviceActions =>
  sortBy(serviceActions, item => item?.actionType?.toLowerCase())
)

/**
 * Selects the serviceAction entries keyed by their id
 * @param {Object} data - The meta filters data
 * @returns {Object} - The serviceAction entries keyed by id
 */
export const selectServiceActionsById = createSelector([selectServiceActions], serviceActions => {
  return keyBy(serviceActions, 'id')
})

/**
 * Selects only the active serviceAction entries
 * @param {Object} data - The meta filters data
 * @returns {Array} - The active serviceAction entries
 */
export const selectActiveServiceActions = createSelector([selectServiceActions], serviceActions => {
  return serviceActions.filter(action => {
    return action.businessLine && action.businessLine.some(line => line.active === true)
  })
})

/**
 * Selectors for meta filters
 * These selectors extract specific data from the meta filters response
 * and format them for use in form fields
 */

// Base selectors

/**
 * Raw data selectors
 * These selectors return the raw data without any transformation
 */

/**
 * Selects the raw measures data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw measures data
 */
export const selectMeasures = createSelector([getMeasures], measures => {
  const listGroupedByMeasure = groupBy(measures, 'unit')
  const sortedUnits = Object.keys(listGroupedByMeasure).sort()
  return sortedUnits.map(unit => sortBy(listGroupedByMeasure[unit], item => parseFloat(item.volume))).flat()
})

/**
 * Selects the raw materials data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw materials data
 */
export const selectMaterials = createSelector([getMaterials], materials => sortBy(materials, item => item?.materialType?.toLowerCase()))

/**
 * Selects the raw methods data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw methods data
 */
export const selectMethods = createSelector([getMethods], methods => sortBy(methods, item => item?.methodType?.toLowerCase()))

/**
 * Selects the raw pricing zones data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw pricing zones data
 */
export const selectPricingZones = createSelector([getPricingZones], zones => sortBy(zones, item => item?.zoneName?.toLowerCase()))

/**
 * Selects the raw business lines data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw business lines data
 */
export const selectBusinessLines = createSelector([getBusinessLines], businessLines =>
  sortBy(businessLines, item => item?.businessLineName?.toLowerCase())
)

/**
 * Selects the raw tax zones data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw tax zones data
 */
export const selectTaxZones = createSelector([getTaxZones], taxZones => sortBy(taxZones, item => item?.zoneName?.toLowerCase()))

/**
 * Selects the raw skills data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw skills data
 */
export const selectSkills = createSelector([getSkills], skills => sortBy(skills, item => item?.skillName?.toLowerCase()))

/**
 * Selects the raw pricing period data
 * @param {Object} data - The meta filters data
 * @returns {Array} - The raw pricing period data
 */
export const selectPricingPeriods = createSelector([getPricingPeriod], periods => {
  const listGroupedByPeriodInterval = groupBy(periods, 'periodInterval')
  const sortedUnits = Object.keys(listGroupedByPeriodInterval).sort()
  return sortedUnits.map(interval => sortBy(listGroupedByPeriodInterval[interval], item => parseFloat(item.periodPer))).flat()
})

/**
 * Selects the measure options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted measure options
 */
export const selectMeasureOptions = createSelector([getMeasures], measures =>
  (measures || []).map(item => ({
    id: item?.id,
    label: item?.measureName,
    value: item?.measureName,
  }))
)

/**
 * Selects the material options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted material options
 */
export const selectMaterialOptions = createSelector([selectMaterials], materials =>
  (materials || []).map(item => ({
    id: item?.id,
    label: item?.materialName,
    value: item?.materialName,
    color: item?.materialColor,
  }))
)

/**
 * Selects the method options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted method options
 */
export const selectMethodOptions = createSelector([selectMethods], methods =>
  (methods || []).map(item => ({
    id: item?.id,
    label: item?.methodName,
    value: item?.methodName,
  }))
)

/**
 * Selects the action options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted action options
 */
export const selectActionOptions = createSelector([selectServiceActions], actions =>
  (actions || []).map(item => ({
    id: item?.id,
    label: item?.actionName,
    value: item?.actionName,
    type: item?.actionType,
  }))
)

/**
 * Selects the pricing zone options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted pricing zone options
 */
export const selectPricingZoneOptions = createSelector([selectPricingZones], zones =>
  (zones || []).map(item => ({
    id: item?.id,
    label: item?.zoneName,
    value: item?.zoneName,
  }))
)

/**
 * Selects the business line options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted business line options
 */
export const selectBusinessLineOptions = createSelector([selectBusinessLines], businessLines =>
  (businessLines || []).map(item => ({
    id: item?.id,
    label: item?.businessLineName,
    value: item?.businessLineName,
  }))
)

/**
 * Selects the active business line options formatted for autocomplete fields and sorted by label
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted active business line options sorted by label
 */
export const selectActiveBusinessLineOptions = createSelector([getBusinessLines], businessLines => {
  const activeBusinessLines = (businessLines || [])
    .filter(({ active }) => active)
    .map(item => ({
      id: item?.id,
      label: item?.businessLineLabel || item?.businessLineName,
      value: item?.id,
    }))

  return sortBy(activeBusinessLines, item => item?.label?.toLowerCase())
})

/**
 * Selects the tax zone options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted tax zone options
 */
export const selectTaxZoneOptions = createSelector([selectTaxZones], zones =>
  (zones || []).map(zone => ({
    id: zone.id,
    label: zone.zoneName,
    value: zone.id,
    default: get(zone, 'default', false),
  }))
)

/**
 * Selects the skill options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted skill options
 */
export const selectSkillOptions = createSelector([selectSkills], skills =>
  (skills || []).map(skill => ({
    id: get(skill, 'id', ''),
    label: get(skill, 'skillName', ''),
    value: get(skill, 'skillName', ''),
  }))
)

/**
 * Selects the pricing period options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted pricing period options
 */
export const selectPricingPeriodOptions = createSelector([selectPricingPeriods], periods =>
  (periods || []).map(period => ({
    id: period.id,
    label: `${period.periodPer} ${period.periodInterval}`,
    value: `${period.periodPer} ${period.periodInterval}`,
    periodPer: period.periodPer,
    periodInterval: period.periodInterval,
  }))
)

/**
 * Selects the recurrence options formatted for autocomplete fields
 * @param {Object} data - The meta filters data
 * @returns {Array} - The formatted recurrence options
 */
export const selectRecurrenceOptions = createSelector([getRecurrence], recurrence => {
  const listGroupedByRecurrenceInterval = groupBy(recurrence, 'recurrenceInterval')
  const sortedUnits = Object.keys(listGroupedByRecurrenceInterval).sort()
  return sortedUnits.map(interval => sortBy(listGroupedByRecurrenceInterval[interval], item => parseFloat(item.recurrencePer))).flat()
})
